import { useEffect, useRef, useState } from "react";
import api from "../lib/api";

// Customer picker backed by the ERP customer master (dbo.party via GET /portal/parties).
// Selecting a party sets BOTH the display name (customer_name, persisted to Mongo) and the
// ERP customer code (portal_pcode, used when the PO is pushed to MS SQL). Free-typing is
// still allowed for customers that aren't in the master.
export function CustomerSelect({ name, pcode, onChange, testid = "customer-select", disabled = false, source = "erp" }) {
  const [query, setQuery] = useState(name || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const boxRef = useRef(null);
  const endpoint = source === "mongo" ? "/customers/search" : "/portal/parties";

  useEffect(() => { setQuery(name || ""); }, [name]);

  useEffect(() => {
    // ERP party master is huge → require 2+ chars. Curated Mongo list is small → browse freely.
    const minLen = source === "mongo" ? 0 : 2;
    if (!open || query.trim().length < minLen) { setResults([]); return; }
    let alive = true;
    setSearching(true);
    const t = setTimeout(() => {
      api.get(`${endpoint}?q=${encodeURIComponent(query.trim())}`)
        .then((r) => { if (alive) setResults(r.data || []); })
        .catch(() => { if (alive) setResults([]); })
        .finally(() => { if (alive) setSearching(false); });
    }, 300);
    return () => { alive = false; clearTimeout(t); };
  }, [query, open, endpoint, source]);

  useEffect(() => {
    const onDoc = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) {
        setOpen(false);
        // In curated mode, revert the box to the actual selected customer (typed-but-unselected
        // text is only a filter and must not be kept as a name).
        if (source === "mongo") setQuery(name || "");
      }
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [source, name]);

  const pick = (p) => {
    onChange({ name: p.name, pcode: p.pcode });
    setQuery(p.name);
    setOpen(false);
    setResults([]);
  };

  return (
    <div ref={boxRef} style={{ position: "relative" }}>
      <label className="jde-label">
        Customer Name
        {pcode && (
          <span className="text-mono" data-testid={`${testid}-pcode`}
                style={{ marginLeft: 8, fontSize: 11, fontWeight: 700, color: "var(--omh-primary, #1E40AF)", background: "#EFF6FF", border: "1px solid #BFDBFE", borderRadius: 4, padding: "1px 6px" }}>
            {pcode}
          </span>
        )}
      </label>
      <input
        className="jde-input"
        value={query}
        disabled={disabled}
        placeholder={source === "mongo" ? "Search your customer list…" : "Type to search the ERP customer list…"}
        data-testid={testid}
        onChange={(e) => {
          setQuery(e.target.value);
          // Mongo (curated) mode: only a real SELECTION sets the customer — free text is
          // just a filter, so no 'trash' names can be entered. ERP mode allows custom text.
          if (source !== "mongo") onChange({ name: e.target.value, pcode });
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
      />
      {open && (query.trim().length >= (source === "mongo" ? 0 : 2)) && (
        <div data-testid={`${testid}-results`}
             style={{ position: "absolute", zIndex: 20, top: "100%", left: 0, right: 0, marginTop: 2,
                      maxHeight: 240, overflowY: "auto", background: "#fff",
                      border: "1px solid var(--jde-border, #cbd5e1)", borderRadius: 6, boxShadow: "0 8px 24px rgba(15,23,42,0.14)" }}>
          {searching && <div style={{ padding: "8px 10px", fontSize: 12, color: "#64748B" }}>Searching…</div>}
          {!searching && results.length === 0 && (
            <div style={{ padding: "8px 10px", fontSize: 12, color: "#64748B" }}>
              {source === "mongo" ? "No matching customer in your list." : "No matching ERP customers — keep typing to use a custom name."}
            </div>
          )}
          {results.map((p) => (
            <div key={p.pcode} onClick={() => pick(p)} data-testid={`${testid}-opt-${p.pcode}`}
                 style={{ padding: "7px 10px", fontSize: 12.5, cursor: "pointer", borderBottom: "1px solid #EEF1F4" }}
                 onMouseEnter={(e) => (e.currentTarget.style.background = "#F1F5F9")}
                 onMouseLeave={(e) => (e.currentTarget.style.background = "#fff")}>
              <span className="text-mono" style={{ fontWeight: 700, color: "var(--omh-primary, #1E40AF)" }}>{p.pcode}</span>
              <span style={{ marginLeft: 8 }}>{p.name}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
